// Pure helpers for the Dashboard. No React, no DOM — testable in isolation.

// ─── Number formatting ────────────────────────────────────────────────────
export const fmtN  = (n) => (n == null ? '—' : Number(n).toLocaleString());
export const fmtH1 = (n) => (n == null ? '—' : `${Number(n).toLocaleString(undefined, { maximumFractionDigits: 1 })}h`);

function pctNum(num, denom) {
  if (!denom) return 0;
  return (num / denom) * 100;
}

export function pctLabel(num, denom) {
  if (!denom) return '0%';
  const v = pctNum(num, denom);
  if (v > 0 && v < 0.1) return '<0.1%';
  if (v < 10) return `${v.toFixed(1)}%`;
  return `${Math.round(v)}%`;
}

// ─── Time / age ───────────────────────────────────────────────────────────
function ageMs(iso) {
  if (!iso) return null;
  return Date.now() - new Date(iso).getTime();
}

function ageHours(iso) {
  const ms = ageMs(iso);
  return ms == null ? null : ms / 3_600_000;
}

export function ageString(iso) {
  const ms = ageMs(iso);
  if (ms == null) return 'Never';
  if (ms < 60_000) return 'just now';
  const mins = Math.floor(ms / 60_000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

// ─── Pipeline bucketing ───────────────────────────────────────────────────
const PIPELINE_BUCKETS = [
  { id: 'dayforce_extract', label: 'Dayforce extract',
    matches: (s) => s === 'adf_dayforce' || s === 'pipeline_dayforce_extract'
                 || s === 'pipeline_dayforce_incremental'
                 || s === 'dayforce_punches' || s === 'dayforce_summaries' },
  { id: 'dayforce_ref',     label: 'Dayforce reference sync',
    matches: (s) => s === 'adf_dayforce_ref_sync' || s === 'pipeline_dayforce_ref_sync' },
  { id: 'netsuite',         label: 'NetSuite sync',
    matches: (s) => s === 'adf_netsuite' || s === 'ns_extractor' || s === 'ns_loader'
                 || s === 'pipeline_ns_extractor' || s === 'pipeline_ns_loader'
                 || s === 'netsuite_sync' },
  { id: 'adls_load',        label: 'ADLS → SQL load',
    matches: (s) => s === 'adls_loader' || s === 'pipeline_adls_loader' || s === 'adls_loader_backfill' },
  { id: 'reprocess',        label: 'Billing reprocess',
    matches: (s) => s === 'reprocess_all' || s === 'reprocess_billing' },
];

export function pipelineHealth(freshness) {
  return PIPELINE_BUCKETS.map((b) => {
    const matched = (freshness || []).filter((f) => b.matches(f.source));
    if (matched.length === 0) return { id: b.id, label: b.label, status: 'unknown' };
    matched.sort((a, b) => {
      const ta = new Date(a.completed_at || a.started_at || 0).getTime();
      const tb = new Date(b.completed_at || b.started_at || 0).getTime();
      return tb - ta;
    });
    const latest = matched[0];
    return {
      id: b.id,
      label: b.label,
      status: latest.status,
      completed_at: latest.completed_at,
      started_at: latest.started_at,
      source: latest.source,
      records_extracted: latest.records_extracted,
      error_message: latest.error_message,
    };
  });
}

export function statusTone(status) {
  if (status === 'completed') return 'green';
  if (status === 'failed')    return 'red';
  if (status === 'running')   return 'navy';
  return 'gray';
}

export function prettySource(source) {
  if (!source) return '—';
  const map = {
    adf_dayforce: 'Dayforce extract',
    adf_dayforce_ref_sync: 'Dayforce ref sync',
    adf_netsuite: 'NetSuite verify',
    pipeline_dayforce_extract: 'Dayforce extract (manual)',
    pipeline_dayforce_incremental: 'Dayforce extract (incremental)',
    pipeline_dayforce_ref_sync: 'Dayforce ref sync (manual)',
    pipeline_ns_extractor: 'NetSuite extract (manual)',
    pipeline_ns_loader: 'NetSuite load (manual)',
    pipeline_adls_loader: 'ADLS load (manual)',
    ns_extractor: 'NetSuite extract',
    ns_loader: 'NetSuite load',
    adls_loader: 'ADLS load',
    adls_loader_backfill: 'ADLS backfill',
    reprocess_all: 'Billing reprocess',
    reprocess_billing: 'Billing reprocess',
  };
  return map[source] || source;
}

// ─── Data Quality rules engine ────────────────────────────────────────────
// Each rule returns { id, dimension, label, status: pass|warn|fail, score: 0-100,
//                    detail, evidence } so a dedicated DQ page can show transparent
// per-rule breakdown and an overall score.
const FRESHNESS_PASS_HOURS = 30;
const FRESHNESS_WARN_HOURS = 48;

function gradeFreshness(label, dimension, isoTs) {
  const hrs = ageHours(isoTs);
  if (hrs == null) {
    return { status: 'fail', score: 0, detail: 'No successful run found in last 30 days.', evidence: 'never' };
  }
  if (hrs <= FRESHNESS_PASS_HOURS) return { status: 'pass', score: 100, detail: `Last successful run ${ageString(isoTs)}.`, evidence: ageString(isoTs) };
  if (hrs <= FRESHNESS_WARN_HOURS) return { status: 'warn', score: 60, detail: `Stale: ${ageString(isoTs)} (expected within ${FRESHNESS_PASS_HOURS}h).`, evidence: ageString(isoTs) };
  return { status: 'fail', score: 0, detail: `Very stale: ${ageString(isoTs)} (expected within ${FRESHNESS_PASS_HOURS}h).`, evidence: ageString(isoTs) };
}

export function dqRules(stats) {
  const rules = [];
  const total = stats.total || 0;
  const pq = stats.push_queue || {};
  const buckets = pipelineHealth(stats.freshness || []);
  const findBucket = (id) => buckets.find((b) => b.id === id);

  // Timeliness — one rule per upstream pipeline.
  for (const id of ['dayforce_extract', 'netsuite', 'adls_load', 'reprocess']) {
    const b = findBucket(id);
    let r;
    if (!b || b.status === 'unknown') {
      r = { status: 'fail', score: 0, detail: 'No batches recorded.', evidence: 'never' };
    } else if (b.status === 'failed') {
      r = { status: 'fail', score: 0, detail: `Most recent run FAILED ${ageString(b.completed_at || b.started_at)}.`, evidence: 'last run failed' };
    } else {
      r = gradeFreshness(b.label, 'timeliness', b.completed_at || b.started_at);
    }
    rules.push({
      id: `timely_${id}`,
      dimension: 'Timeliness',
      label: `${b ? b.label : id} ran successfully recently`,
      ...r,
    });
  }

  // Correctness — mapping coverage and reference data integrity.
  if (total > 0) {
    const gapRate = pctNum(stats.gap || 0, total);
    let r;
    if (gapRate < 15) r = { status: 'pass', score: 100, detail: `Mapping gap rate ${pctLabel(stats.gap || 0, total)} — within tolerance.`, evidence: pctLabel(stats.gap || 0, total) };
    else if (gapRate < 25) r = { status: 'warn', score: 60, detail: `Mapping gap rate ${pctLabel(stats.gap || 0, total)} — above 15% threshold.`, evidence: pctLabel(stats.gap || 0, total) };
    else r = { status: 'fail', score: 0, detail: `Mapping gap rate ${pctLabel(stats.gap || 0, total)} — far above acceptable threshold.`, evidence: pctLabel(stats.gap || 0, total) };
    rules.push({
      id: 'mapping_coverage', dimension: 'Correctness',
      label: 'Mapping coverage (gap rate < 15%)',
      ...r,
    });
  }

  // Failure signals — record-level + push-level.
  const failed = stats.failed || 0;
  rules.push({
    id: 'no_failed_records', dimension: 'Correctness',
    label: 'No records in failed status',
    status: failed === 0 ? 'pass' : 'fail',
    score: failed === 0 ? 100 : 0,
    detail: failed === 0 ? 'No records currently failed.' : `${failed} record(s) in failed status.`,
    evidence: `${failed} failed`,
  });

  const failedPush = pq.failed || 0;
  rules.push({
    id: 'no_failed_pushes', dimension: 'Correctness',
    label: 'No NetSuite sync failures',
    status: failedPush === 0 ? 'pass' : 'fail',
    score: failedPush === 0 ? 100 : 0,
    detail: failedPush === 0 ? 'No failed NetSuite syncs.' : `${failedPush} NetSuite sync(s) failed.`,
    evidence: `${failedPush} failed syncs`,
  });

  // Completeness — open alerts + records present at all.
  const openAlerts = (stats.open_alerts || []).length;
  rules.push({
    id: 'no_open_alerts', dimension: 'Completeness',
    label: 'No unacknowledged alerts',
    status: openAlerts === 0 ? 'pass' : (openAlerts < 3 ? 'warn' : 'fail'),
    score: openAlerts === 0 ? 100 : (openAlerts < 3 ? 60 : 0),
    detail: openAlerts === 0 ? 'Alert log is clean.' : `${openAlerts} open alert(s) — see Audit Log.`,
    evidence: `${openAlerts} open`,
  });

  rules.push({
    id: 'records_present', dimension: 'Completeness',
    label: 'Billing records exist',
    status: total > 0 ? 'pass' : 'fail',
    score: total > 0 ? 100 : 0,
    detail: total > 0 ? `${fmtN(total)} records present.` : 'No billing records — pipeline may not have run.',
    evidence: `${fmtN(total)} records`,
  });

  return rules;
}

export function dqScore(rules) {
  if (!rules || rules.length === 0) return { score: 0, label: 'Unknown', tone: 'gray' };
  const sum = rules.reduce((a, r) => a + (r.score || 0), 0);
  const score = Math.round(sum / rules.length);
  let label, tone;
  if (score >= 90)      { label = 'Healthy';        tone = 'green'; }
  else if (score >= 70) { label = 'Watch';          tone = 'orange'; }
  else if (score >= 40) { label = 'Degraded';       tone = 'red'; }
  else                  { label = 'Critical';       tone = 'red'; }
  return { score, label, tone };
}

// ─── Smart narrative bullets ──────────────────────────────────────────────
export function narrativeForOverview(stats) {
  const lines = [];
  const total = stats.total || 0;
  if (total === 0) {
    lines.push('No billing records yet. Pull data from Dayforce via Data Extraction to get started.');
    return lines;
  }
  const pending = stats.pending || 0;
  const gap = stats.gap || 0;
  const billable = stats.billable_hours || 0;
  const gapHours = stats.gap_hours || 0;
  const excluded = stats.excluded_ref_data || 0;
  const pq = stats.push_queue || {};
  const failedPush = pq.failed || 0;
  const queued = stats.queued || 0;

  if (pending > 0) {
    lines.push(`${fmtN(pending)} records ready for review${billable > 0 ? ` — ${fmtN(Math.round(billable))} billable hours.` : '.'}`);
  } else {
    lines.push('Nothing currently waiting for review.');
  }
  if (gap > 0) {
    lines.push(`${fmtN(gap)} records blocked by mapping gaps (${fmtN(Math.round(gapHours))}h). Fix on the Blockers page before pushing.`);
  }
  if (excluded > 0) {
    lines.push(`${fmtN(excluded)} records were intentionally skipped by reference data rules — check periodically that exclusions are still correct.`);
  }
  if (failedPush > 0) {
    lines.push(`${failedPush} NetSuite push${failedPush === 1 ? '' : 'es'} failed — investigate from Push Performance.`);
  } else if (queued > 0) {
    lines.push(`${fmtN(queued)} records queued for NetSuite — push from the Records tab.`);
  }
  return lines;
}

export function narrativeForBlockers(stats) {
  const total = stats.total || 0;
  const gap = stats.gap || 0;
  const gapHrs = stats.gap_hours || 0;
  if (gap === 0) return ['No blocked records — all current data has matched a NetSuite mapping.'];
  return [
    `${fmtN(gap)} records (${fmtN(Math.round(gapHrs))} worked hours) cannot be billed because no NetSuite mapping was found.`,
    `That is ${pctLabel(gap, total)} of all current records — the rest mapped correctly.`,
    'Most common causes: a new Dayforce labor metric, a deactivated NetSuite reference, or a typo in a project/task code.',
  ];
}

export function narrativeForTrends(trend) {
  if (!trend || trend.length < 2) return ['Not enough history to describe a trend yet.'];
  const recent = trend.slice(-7);
  const prior = trend.slice(-14, -7);
  const sum = (xs, k) => xs.reduce((a, b) => a + (b[k] || 0), 0);
  const recentTotal = sum(recent, 'total');
  const priorTotal = sum(prior, 'total');
  const recentGap = sum(recent, 'gap');
  const priorGap = sum(prior, 'gap');
  const lines = [];
  if (priorTotal > 0) {
    const delta = ((recentTotal - priorTotal) / priorTotal) * 100;
    lines.push(`Volume ${delta >= 0 ? 'up' : 'down'} ${Math.abs(delta).toFixed(0)}% over the last 7 days vs the prior 7.`);
  }
  if (priorGap > 0) {
    const delta = ((recentGap - priorGap) / priorGap) * 100;
    if (Math.abs(delta) >= 5) {
      lines.push(`Mapping gaps ${delta >= 0 ? 'up' : 'down'} ${Math.abs(delta).toFixed(0)}% week-over-week.`);
    } else {
      lines.push('Mapping gaps roughly steady week-over-week.');
    }
  }
  return lines.length > 0 ? lines : ['No notable change in the last 7 days.'];
}

// ─── Sub-page registry ────────────────────────────────────────────────────
// Single source of truth for sub-page id → label + help text.
export const SUB_PAGES = [
  { id: 'overview',     label: 'Overview',
    help: 'A snapshot of the current billing operation. Use the cards here to jump into any specific area.' },
  { id: 'pipeline',     label: 'Pipeline Health',
    help: 'Status of every upstream pipeline that feeds the dashboard. If anything here is red or stale, the numbers below it are suspect.' },
  { id: 'push_today',   label: 'Sync Today',
    help: 'What is currently approvable, queued, or already synced today. Use this view to drive your daily approve-and-sync workflow.' },
  { id: 'blockers',     label: 'Blockers',
    help: 'Records that cannot be billed because a mapping or reference is missing. Each row is a real punch waiting on a fix.' },
  { id: 'trends',       label: 'Trends',
    help: 'How volume, gaps, and exclusions are changing day-by-day. Use this to spot drift before it becomes a problem.' },
  { id: 'locations',    label: 'By Location',
    help: 'Which locations are contributing the most pending work and the most blocked records.' },
  { id: 'watchlist',    label: 'OT Watchlist',
    help: 'Employees flagged for customer overtime. These records may need extra scrutiny before billing.' },
  { id: 'data_quality', label: 'Data Quality',
    help: 'Truth-in-data: every rule we check for completeness, correctness, and timeliness. Each rule has a pass/warn/fail outcome that rolls up into the DQ score.' },
];
